import { useCallback, useEffect, useMemo } from "react";
import { HashRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import DashboardPage from "./pages/DashboardPage";
import PlayDatesMatchesApp from "./play-dates/TennisMatchApp";
import PlayDatesInvitationPage from "./play-dates/InvitationPage";
import PlayDatesMatchPage from "./play-dates/pages/MatchPage";
import PlayDatesResetPasswordPage from "./play-dates/pages/ResetPassword";
import PlayDatesCreateMatchPage from "./play-dates/pages/CreateMatchPage";
import PlayDatesCourtFinderPage from "./play-dates/pages/CourtFinder";
import PlayDatesMatchSuccessPreview from "./play-dates/pages/MatchSuccessPreview";
import {
  QueryClientProvider as PlayDatesQueryClientProvider,
  createQueryClient as createPlayDatesQueryClient,
} from "./play-dates/utils/react-query-shim";
import AppNav from "./components/AppNav";
import CreateMatchPage from "./pages/CreateMatchPage";
import CreateMatchSettingsPage from "./pages/CreateMatchSettingsPage";
import CreateMatchReviewPage from "./pages/CreateMatchReviewPage";
import CreateMatchPublishConfirmationPage from "./pages/CreateMatchPublishConfirmationPage";
import CreatePrivateMatchInvitePage from "./pages/CreatePrivateMatchInvitePage";
import FindCoaches from "./pages/FindCoaches";
import CoachMatchRecommendationsPage from "./pages/CoachMatchRecommendationsPage";
import FindPlayersPage from "./pages/FindPlayersPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import LoginPage from "./pages/LoginPage";
import LessonInvitePage from "./pages/LessonInvitePage";
import PlayerCoachListPage from "./pages/PlayerCoachListPage";
import CoachProfilePage from "./pages/CoachProfilePage";
import PlayerProfilePage from "./pages/PlayerProfilePage";
import AccountProfilePage from "./pages/AccountProfilePage";
import PlayerMatchProfilePage from "./pages/PlayerMatchProfilePage";
import PlayerMatchProfileEditPage from "./pages/PlayerMatchProfileEditPage";
import PaymentMethodsPage from "./pages/PaymentMethodsPage";
import BlockedUsersPage from "./pages/BlockedUsersPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import PurchaseLessonPackagePage from "./pages/PurchaseLessonPackagePage";
import GroupLessonsPage from "./pages/GroupLessonsPage";
import GroupLessonDetailsPage from "./pages/GroupLessonDetailsPage";
import PlayerLessonDetailsPage from "./pages/PlayerLessonDetailsPage";
import MyCoachesPage from "./pages/MyCoachesPage";
import CreditsPage from "./pages/CreditsPage";
import NotificationsPage from "./pages/NotificationsPage";
import PlayerCalendar from "./screens/Player/PlayerCalendar";
import MobileHomeBottomNav from "./components/MobileHomeBottomNav";
import "./App.css";

const playDatesQueryClient = createPlayDatesQueryClient();

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading-screen">Loading your dashboard…</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

const AuthRedirectRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading-screen">Preparing the experience…</div>;
  }

  if (isAuthenticated) {
    const from = location.state?.from;
    const to = from
      ? {
          pathname: from.pathname || "/",
          search: from.search || "",
          hash: from.hash || "",
        }
      : "/";
    return <Navigate to={to} replace state={from?.state} />;
  }

  return children;
};

const readStoredObject = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const buildMatchesUser = (authUser) => {
  const personalDetails = readStoredObject("playerPersonalDetails");
  const loginResponse = readStoredObject("authLoginResponse");
  const profile = authUser || personalDetails || loginResponse?.profile || loginResponse?.user || {};

  return {
    ...profile,
    id: profile?.id ?? loginResponse?.user_id ?? loginResponse?.id ?? null,
    type: profile?.user_type ?? loginResponse?.user_type ?? 2,
    name:
      profile?.name ||
      profile?.full_name ||
      profile?.fullName ||
      loginResponse?.full_name ||
      profile?.email ||
      "Player",
    email: profile?.email || loginResponse?.email || "",
    phone: profile?.phone || profile?.mobile || loginResponse?.phone || "",
    skillLevel:
      profile?.skillLevel ||
      profile?.skill_level ||
      profile?.usta_rating ||
      loginResponse?.skillLevel ||
      "",
    profile,
  };
};

const PlayDatesAppRoute = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const matchesUser = useMemo(() => buildMatchesUser(user), [user]);
  const shouldOpenCreateOnReady = Boolean(location.state?.openNewMatch);

  useEffect(() => {
    try {
      localStorage.setItem("user", JSON.stringify(matchesUser));
    } catch {
      // ignore storage errors
    }
  }, [matchesUser]);

  const openNewMatch = useCallback(() => {
    window.dispatchEvent(new CustomEvent("play-dates:new-match"));
  }, []);

  return (
    <ProtectedRoute>
      <div className="dashboard-page">
        <AppNav
          onNewMatch={openNewMatch}
          hideMobileNewMatch
          hideMobileNotifications
        />
        <main className="main-layout__content">
          <PlayDatesQueryClientProvider client={playDatesQueryClient}>
            <PlayDatesMatchesApp
              externalUser={matchesUser}
              onExternalLogout={logout}
              hideAppHeader
              openCreateOnReady={shouldOpenCreateOnReady}
              onCreateReadyHandled={() => navigate("/matches", { replace: true })}
            />
          </PlayDatesQueryClientProvider>
        </main>
        <MobileHomeBottomNav />
      </div>
    </ProtectedRoute>
  );
};

const PlayDatesProtectedPageRoute = ({ children }) => (
  <ProtectedRoute>
    <div className="dashboard-page">
      <AppNav />
      <main className="main-layout__content">
        <PlayDatesQueryClientProvider client={playDatesQueryClient}>
          {children}
        </PlayDatesQueryClientProvider>
      </main>
    </div>
  </ProtectedRoute>
);

const PlayDatesPublicPageRoute = ({ children }) => (
  <PlayDatesQueryClientProvider client={playDatesQueryClient}>
    {children}
  </PlayDatesQueryClientProvider>
);

const AppRoutes = () => (
  <Routes>
    <Route
      path="/login"
      element={(
        <AuthRedirectRoute>
          <LoginPage />
        </AuthRedirectRoute>
      )}
    />
    <Route
      path="/forgot-password"
      element={(
        <AuthRedirectRoute>
          <ForgotPasswordPage />
        </AuthRedirectRoute>
      )}
    />
    <Route
      path="/li/:token"
      element={<LessonInvitePage />}
    />
    <Route
      path="/"
      element={(
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/matches"
      element={<PlayDatesAppRoute />}
    />
    <Route
      path="/matches/:id/invite"
      element={<PlayDatesAppRoute />}
    />
    <Route
      path="/matches/create"
      element={(
        <ProtectedRoute>
          <CreateMatchPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/matches/create/settings"
      element={(
        <ProtectedRoute>
          <CreateMatchSettingsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/matches/create/private/invite"
      element={(
        <ProtectedRoute>
          <CreatePrivateMatchInvitePage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/matches/create/review"
      element={(
        <ProtectedRoute>
          <CreateMatchReviewPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/matches/create/published"
      element={(
        <ProtectedRoute>
          <CreateMatchPublishConfirmationPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/matches/:id"
      element={(
        <PlayDatesPublicPageRoute>
          <PlayDatesMatchPage />
        </PlayDatesPublicPageRoute>
      )}
    />
    <Route
      path="/create"
      element={(
        <PlayDatesProtectedPageRoute>
          <PlayDatesCreateMatchPage />
        </PlayDatesProtectedPageRoute>
      )}
    />
    <Route
      path="/courts"
      element={(
        <PlayDatesProtectedPageRoute>
          <PlayDatesCourtFinderPage />
        </PlayDatesProtectedPageRoute>
      )}
    />
    <Route
      path="/invites"
      element={<PlayDatesAppRoute />}
    />
    <Route
      path="/players"
      element={<PlayDatesAppRoute />}
    />
    <Route
      path="/groups"
      element={<PlayDatesAppRoute />}
    />
    <Route
      path="/groups/:id"
      element={<PlayDatesAppRoute />}
    />
    <Route
      path="/invites/:token"
      element={(
        <PlayDatesPublicPageRoute>
          <PlayDatesInvitationPage />
        </PlayDatesPublicPageRoute>
      )}
    />
    <Route
      path="/i/:token"
      element={(
        <PlayDatesPublicPageRoute>
          <PlayDatesInvitationPage />
        </PlayDatesPublicPageRoute>
      )}
    />
    <Route
      path="/m/:token"
      element={(
        <PlayDatesPublicPageRoute>
          <PlayDatesInvitationPage />
        </PlayDatesPublicPageRoute>
      )}
    />
    <Route
      path="/reset-password/:token"
      element={(
        <PlayDatesPublicPageRoute>
          <PlayDatesResetPasswordPage />
        </PlayDatesPublicPageRoute>
      )}
    />
    {import.meta.env.DEV ? (
      <Route
        path="/__preview/match-success"
        element={(
          <PlayDatesPublicPageRoute>
            <PlayDatesMatchSuccessPreview />
          </PlayDatesPublicPageRoute>
        )}
      />
    ) : null}
    <Route
      path="/coaches"
      element={(
        <ProtectedRoute>
          <PlayerCoachListPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/my-coaches"
      element={(
        <ProtectedRoute>
          <MyCoachesPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/find-coaches"
      element={(
        <ProtectedRoute>
          <FindCoaches />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/coach-match/recommendations"
      element={(
        <ProtectedRoute>
          <CoachMatchRecommendationsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/credits"
      element={(
        <ProtectedRoute>
          <CreditsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/notifications"
      element={(
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/find-players"
      element={(
        <ProtectedRoute>
          <FindPlayersPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/players/:id"
      element={(
        <ProtectedRoute>
          <PlayerProfilePage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/coaches/:id"
      element={<CoachProfilePage />}
    />
    <Route
      path="/coaches/:id/purchase"
      element={<PurchaseLessonPackagePage />}
    />
    <Route
      path="/group-lessons"
      element={(
        <ProtectedRoute>
          <GroupLessonsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/group-lessons/:id"
      element={(
        <ProtectedRoute>
          <GroupLessonDetailsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/player/lesson/:id"
      element={(
        <ProtectedRoute>
          <PlayerLessonDetailsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/player/calendar"
      element={(
        <ProtectedRoute>
          <PlayerCalendar />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/settings/profile"
      element={(
        <ProtectedRoute>
          <AccountProfilePage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/settings/match-profile"
      element={(
        <ProtectedRoute>
          <PlayerMatchProfilePage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/settings/match-profile/edit"
      element={(
        <ProtectedRoute>
          <PlayerMatchProfileEditPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/match-profile"
      element={(
        <ProtectedRoute>
          <PlayerMatchProfilePage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/match-profile/edit"
      element={(
        <ProtectedRoute>
          <PlayerMatchProfileEditPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/settings/payment-methods"
      element={(
        <ProtectedRoute>
          <PaymentMethodsPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/settings/blocked-users"
      element={(
        <ProtectedRoute>
          <BlockedUsersPage />
        </ProtectedRoute>
      )}
    />
    <Route
      path="/booking/confirm"
      element={(
        <ProtectedRoute>
          <BookingConfirmationPage />
        </ProtectedRoute>
      )}
    />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  return (
    <AuthProvider>
      <HashRouter>
        <div className="app-shell">
          <AppRoutes />
        </div>
      </HashRouter>
    </AuthProvider>
  );
}

export default App;
